import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Select, Store } from '@ngxs/store';
import { uniqBy } from 'lodash-es';
import * as moment from 'moment';
import { Observable, withLatestFrom } from 'rxjs';
import { BankAccountType, PaymentMethod, PaymentMethodType, UsState } from 'src/app/core/models';
import { LookupState } from 'src/app/core/store/lookup/lookup.state';
import { CreatePatientPaymentMethod } from 'src/app/core/store/payment-methods/payment-methods.actions';
import { PaymentMethodsState } from 'src/app/core/store/payment-methods/payment-methods.state';
import { ApiStoreData } from 'src/app/core/store/utils';
import { asFormControl, getCreditCardProcessor } from 'src/app/core/utils/common';
import { FormatAddressPipe } from 'src/app/core/utils/pipes';
import {
  bankAccountRoutingNumberValidator,
  creditCardCvvDigitsValidator,
  creditCardCvvLengthValidator,
  creditCardExpirationValidator,
  creditCardNumberValidator,
  validateAllFormFields,
  zipCodeValidator,
} from 'src/app/core/utils/validators';
import { FormFieldOption } from 'src/app/shared/form-field/form-field.component';

const CREDIT_CARD_NUMBER_VALIDATORS = [Validators.required, creditCardNumberValidator()];
const CREDIT_CARD_EXP_MONTH_VALIDATORS = [Validators.required, creditCardExpirationValidator()];
const CREDIT_CARD_CVV_VALIDATORS = [
  Validators.required,
  creditCardCvvLengthValidator(),
  creditCardCvvDigitsValidator(),
];
const BANK_ACCOUNT_NUMBER_VALIDATORS = [Validators.required, Validators.minLength(8), Validators.maxLength(18)];
const BANK_ACCOUNT_ROUTING_NUMBER_VALIDATORS = [Validators.required, bankAccountRoutingNumberValidator()];

@Component({
  selector: 'app-payment-method-form',
  templateUrl: './payment-method-form.component.html',
  styleUrls: ['./payment-method-form.component.scss'],
})
export class PaymentMethodFormComponent implements OnInit {
  @Input() visible!: boolean;
  @Output() visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() submitForm: EventEmitter<string> = new EventEmitter<string>();

  @Select(PaymentMethodsState.paymentMethods) paymentMethods$!: Observable<PaymentMethod[]>;
  @Select(PaymentMethodsState.createPaymentMethodState) createPaymentMethodState$!: Observable<
    ApiStoreData<PaymentMethod | null>
  >;
  @Select(LookupState.usStates) usStates$!: Observable<UsState[]>;

  initialValues = {
    firstName: null,
    lastName: null,
    isDefault: false,
    addressPaymentMethodId: null,
    addressLine1: null,
    addressLine2: null,
    addressCity: null,
    addressState: null,
    addressZipCode: null,
    type: PaymentMethodType.CreditCard,
    creditCardInfo: {
      number: null,
      processor: null,
      expirationMonth: null,
      expirationYear: null,
      cvv: null,
      isHealthAccount: false,
    },
    bankAccountInfo: {
      accountNumber: null,
      routingNumber: null,
      accountType: BankAccountType.Checking,
    },
  };

  newPaymentMethodForm = new FormGroup({
    firstName: new FormControl(this.initialValues.firstName, [Validators.required]),
    lastName: new FormControl(this.initialValues.lastName, [Validators.required]),
    isDefault: new FormControl(this.initialValues.isDefault),
    // Address
    addressPaymentMethodId: new FormControl(this.initialValues.addressPaymentMethodId),
    addressLine1: new FormControl(this.initialValues.addressLine1),
    addressLine2: new FormControl(this.initialValues.addressLine2),
    addressCity: new FormControl(this.initialValues.addressCity),
    addressState: new FormControl(this.initialValues.addressState),
    addressZipCode: new FormControl(this.initialValues.addressZipCode),
    type: new FormControl(this.initialValues.type),
    creditCardInfo: new FormGroup({
      number: new FormControl(this.initialValues.creditCardInfo.number, CREDIT_CARD_NUMBER_VALIDATORS),
      processor: new FormControl(this.initialValues.creditCardInfo.processor, [Validators.required]),
      expirationMonth: new FormControl(
        this.initialValues.creditCardInfo.expirationMonth,
        CREDIT_CARD_EXP_MONTH_VALIDATORS
      ),
      expirationYear: new FormControl(this.initialValues.creditCardInfo.expirationYear, [Validators.required]),
      cvv: new FormControl(this.initialValues.creditCardInfo.cvv, CREDIT_CARD_CVV_VALIDATORS),
      isHealthAccount: new FormControl(this.initialValues.creditCardInfo.isHealthAccount),
    }),
    bankAccountInfo: new FormGroup({
      accountNumber: new FormControl(this.initialValues.bankAccountInfo.accountNumber),
      routingNumber: new FormControl(this.initialValues.bankAccountInfo.routingNumber),
      accountType: new FormControl(this.initialValues.bankAccountInfo.accountType),
    }),
  });

  readonly PaymentMethodType = PaymentMethodType;
  paymentMethodTypeOptions: FormFieldOption[] = [];
  bankAccountOptions: FormFieldOption[] = [];
  expirationMonthOptions: FormFieldOption[] = [];
  expirationYearOptions: FormFieldOption[] = [];
  addressOptions: FormFieldOption[] = [];
  usStateOptions: FormFieldOption[] = [];
  processorLogoUrl: string | null = null;
  isNewAddress: boolean = false;

  asFormControl = asFormControl;

  constructor(private store: Store, private formatAddressPipe: FormatAddressPipe) {}

  ngOnInit(): void {
    this.setDropdownOptions();
    this.setFormListeners();
  }

  setDropdownOptions() {
    // NSSETP-375: The business wants to temporarily remove the ability to use electronic checks due to issues with the payment processor
    // This leaves just the credit card option temporarily until the issue is resolved
    this.paymentMethodTypeOptions = [
      { label: 'Credit Card', value: PaymentMethodType.CreditCard },
      { label: 'Health Savings Account', value: PaymentMethodType.HealthSavingsAccount },
      // { label: 'Electronic Check', value: PaymentMethodType.BankAccount },
    ];

    this.bankAccountOptions = [
      { label: 'Checking', value: BankAccountType.Checking },
      { label: 'Savings', value: BankAccountType.Savings },
    ];

    this.expirationMonthOptions = [...Array(12).keys()].map((n) => {
      const month = String(n + 1).padStart(2, '0');

      return {
        label: month,
        value: month,
      };
    });

    const currentYear = moment().year();

    this.expirationYearOptions = [...Array(10).keys()].map((i) => {
      const year = currentYear + i;

      return {
        label: year.toString(),
        value: year,
      };
    });

    this.usStates$.subscribe((usStates) => {
      this.usStateOptions = usStates.map((s) => ({
        label: s.name,
        value: s.value,
      }));
    });

    this.paymentMethods$.subscribe((paymentMethods) => {
      if (paymentMethods.length > 0) {
        const allAddressOptions = paymentMethods.map((pm) => ({
          label: this.formatAddressPipe.transform(pm.address, { singleLine: true }),
          value: pm.id,
        }));

        this.addressOptions = uniqBy(allAddressOptions, 'label');
        this.useExistingAddress();
      } else {
        this.useNewAddress();
      }
    });
  }

  setFormListeners() {
    this.newPaymentMethodForm.get('type')?.valueChanges.subscribe((type) => {
      if (type === PaymentMethodType.CreditCard || type === PaymentMethodType.HealthSavingsAccount) {
        this.useCreditCard();
        // Set isHealthAccount based on payment method type
        const isHSA = type === PaymentMethodType.HealthSavingsAccount;
        this.newPaymentMethodForm.get('creditCardInfo')?.get('isHealthAccount')?.setValue(isHSA);
      } else if (type === PaymentMethodType.BankAccount) {
        this.useBankAccount();
      }
    });

    this.newPaymentMethodForm
      .get('creditCardInfo')
      ?.get('number')
      ?.valueChanges.subscribe((creditCardNumber) => {
        const processor = getCreditCardProcessor(creditCardNumber);
        this.newPaymentMethodForm
          .get('creditCardInfo')
          ?.get('processor')
          ?.setValue(processor?.value || null);
        this.newPaymentMethodForm.get('creditCardInfo')?.get('number')?.updateValueAndValidity({ emitEvent: false });
        this.processorLogoUrl = processor?.logoUrl || null;

        if (this.newPaymentMethodForm.get('creditCardInfo')?.get('cvv')!.touched) {
          this.newPaymentMethodForm.get('creditCardInfo')?.get('cvv')?.updateValueAndValidity();
        }
      });

    this.newPaymentMethodForm
      .get('creditCardInfo')
      ?.get('expirationYear')
      ?.valueChanges.subscribe((_) => {
        if (this.newPaymentMethodForm.get('creditCardInfo')?.get('expirationMonth')!.touched) {
          this.newPaymentMethodForm.get('creditCardInfo')?.get('expirationMonth')?.updateValueAndValidity();
        }
      });
  }

  onCancel() {
    this.exitForm();
  }

  onSubmit() {
    if (this.newPaymentMethodForm.valid) {
      this.store
        .dispatch(new CreatePatientPaymentMethod(this.newPaymentMethodForm.value))
        .pipe(withLatestFrom(this.createPaymentMethodState$))
        .subscribe(([_, createPaymentMethodState]) => {
          if (!createPaymentMethodState.error) {
            this.submitForm.emit(createPaymentMethodState.data?.id || '-1');
            this.exitForm();
          } else {
            const dialogContent = document.getElementsByClassName('p-dialog-content')[0];
            if (dialogContent) {
              dialogContent.scroll({ top: 0, left: 0, behavior: 'smooth' });
            }
          }
        });
    } else {
      validateAllFormFields(this.newPaymentMethodForm);
    }
  }

  exitForm() {
    this.newPaymentMethodForm.reset();
    this.newPaymentMethodForm.setValue(this.initialValues);
    this.hideModal();
  }

  onVisibleChange(value: boolean) {
    this.visibleChange.emit(value);
  }

  hideModal() {
    this.visible = false;
    this.onVisibleChange(this.visible);
  }

  showModal() {
    this.visible = true;
    this.onVisibleChange(this.visible);
  }

  useNewAddress() {
    // Validators must be updated first before resetting to update the entire state for each
    this.newPaymentMethodForm.get('addressPaymentMethodId')!.setValidators([]);
    this.newPaymentMethodForm.get('addressLine1')!.setValidators([Validators.required]);
    this.newPaymentMethodForm.get('addressCity')!.setValidators([Validators.required]);
    this.newPaymentMethodForm.get('addressState')!.setValidators([Validators.required]);
    this.newPaymentMethodForm.get('addressZipCode')!.setValidators([Validators.required, zipCodeValidator()]);
    this.newPaymentMethodForm.get('addressPaymentMethodId')!.reset();
    this.newPaymentMethodForm.get('addressLine1')!.reset();
    this.newPaymentMethodForm.get('addressLine2')!.reset();
    this.newPaymentMethodForm.get('addressCity')!.reset();
    this.newPaymentMethodForm.get('addressState')!.reset();
    this.isNewAddress = true;
  }

  useExistingAddress() {
    this.newPaymentMethodForm.get('addressPaymentMethodId')!.setValidators([Validators.required]);
    this.newPaymentMethodForm.get('addressLine1')!.setValidators([]);
    this.newPaymentMethodForm.get('addressCity')!.setValidators([]);
    this.newPaymentMethodForm.get('addressState')!.setValidators([]);
    this.newPaymentMethodForm.get('addressZipCode')!.setValidators([]);
    this.newPaymentMethodForm.get('addressPaymentMethodId')!.reset();
    this.newPaymentMethodForm.get('addressLine1')!.reset();
    this.newPaymentMethodForm.get('addressLine2')!.reset();
    this.newPaymentMethodForm.get('addressCity')!.reset();
    this.newPaymentMethodForm.get('addressState')!.reset();
    this.newPaymentMethodForm.get('addressZipCode')!.reset();
    this.isNewAddress = false;
  }

  useCreditCard() {
    this.newPaymentMethodForm.get('bankAccountInfo')?.get('accountNumber')?.setValidators([]);
    this.newPaymentMethodForm.get('bankAccountInfo')?.get('routingNumber')?.setValidators([]);
    this.newPaymentMethodForm.get('bankAccountInfo')?.get('accountType')?.setValidators([]);
    this.newPaymentMethodForm.get('creditCardInfo')?.get('number')?.setValidators(CREDIT_CARD_NUMBER_VALIDATORS);
    this.newPaymentMethodForm.get('creditCardInfo')?.get('processor')?.setValidators([Validators.required]);
    this.newPaymentMethodForm
      .get('creditCardInfo')
      ?.get('expirationMonth')
      ?.setValidators(CREDIT_CARD_EXP_MONTH_VALIDATORS);
    this.newPaymentMethodForm.get('creditCardInfo')?.get('expirationYear')?.setValidators([Validators.required]);
    this.newPaymentMethodForm.get('creditCardInfo')?.get('cvv')?.setValidators(CREDIT_CARD_CVV_VALIDATORS);
    this.newPaymentMethodForm.get('bankAccountInfo')?.reset();

    // Don't reset creditCardInfo entirely to preserve isHealthAccount value
    const isHealthAccount = this.newPaymentMethodForm.get('creditCardInfo')?.get('isHealthAccount')?.value;
    this.newPaymentMethodForm.get('creditCardInfo')?.reset({
      isHealthAccount: isHealthAccount,
    });
  }

  useBankAccount() {
    this.newPaymentMethodForm
      .get('bankAccountInfo')
      ?.get('accountNumber')
      ?.setValidators(BANK_ACCOUNT_NUMBER_VALIDATORS);
    this.newPaymentMethodForm
      .get('bankAccountInfo')
      ?.get('routingNumber')
      ?.setValidators(BANK_ACCOUNT_ROUTING_NUMBER_VALIDATORS);
    this.newPaymentMethodForm.get('bankAccountInfo')?.get('accountType')?.setValidators([Validators.required]);
    this.newPaymentMethodForm.get('creditCardInfo')?.get('number')?.setValidators([]);
    this.newPaymentMethodForm.get('creditCardInfo')?.get('processor')?.setValidators([]);
    this.newPaymentMethodForm.get('creditCardInfo')?.get('expirationMonth')?.setValidators([]);
    this.newPaymentMethodForm.get('creditCardInfo')?.get('expirationYear')?.setValidators([]);
    this.newPaymentMethodForm.get('creditCardInfo')?.get('cvv')?.setValidators([]);
    this.newPaymentMethodForm.get('creditCardInfo')?.get('isHealthAccount')?.setValidators([]);
    this.newPaymentMethodForm.get('bankAccountInfo')?.reset();
    this.newPaymentMethodForm.get('creditCardInfo')?.reset();
  }
}
